import React, { useState, useEffect } from "react";
import logo from "../assets/logo_nobg.png";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

function getYouTubeId(url) {
  const m = url?.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^&?/\s]+)/);
  return m ? m[1] : null;
}

function readTime(blocks) {
  const text = (blocks || []).map((b) => b.content || "").join(" ");
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setPct(total > 0 ? Math.min(100, (el.scrollTop / total) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 3, zIndex: 100, background: "rgba(255,255,255,0.06)" }}>
      <div style={{ height: "100%", width: `${pct}%`, background: "#C7F94B", transition: "width 0.1s linear" }} />
    </div>
  );
}

export default function FinanceBlog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    getDoc(doc(db, "blogs", id)).then((snap) => {
      if (snap.exists()) setBlog({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const date = blog?.createdAt?.toDate?.()?.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  const mins = readTime(blog?.blocks);

  if (loading) {
    return (
      <div className="fb fb--center">
        <style>{CSS}</style>
        <div className="fb-spinner" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="fb fb--center">
        <style>{CSS}</style>
        <div className="fb-empty">
          <span className="fb-empty-icon">◈</span>
          <p>Post not found.</p>
          <button className="fb-btn fb-btn--outline" onClick={() => navigate("/")}>← Back to Finance</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fb">
      <style>{CSS}</style>
      <ReadingProgress />

      <nav className={`fb-nav${scrolled ? " fb-nav--solid" : ""}`}>
        <div className="fb-nav-inner">
          <button className="fb-nav-back" onClick={() => navigate("/")}>← All Articles</button>
          {scrolled && <span className="fb-nav-title">{blog.title}</span>}
          <img src={logo} alt="S/U" className="fb-nav-logo" />
        </div>
      </nav>

      <div className="fb-page">
        <div className="fb-container">

          <header className="fb-header">
            <span className="fb-chip">Finance &amp; Markets</span>
            <h1 className="fb-title">{blog.title}</h1>
            <div className="fb-meta-row">
              <div className="fb-author">
                <div className="fb-author-avatar">SU</div>
                <div>
                  <div className="fb-author-name">Shashank Upadhyaya</div>
                  <div className="fb-author-meta">{date}{date && " · "}{mins} min read</div>
                </div>
              </div>
              {blog.reelUrl && (
                <a className="fb-btn fb-btn--gold" href={blog.reelUrl} target="_blank" rel="noreferrer">
                  ▶ Watch the Reel
                </a>
              )}
            </div>
          </header>

          {blog.thumbnail && (
            <div className="fb-thumbnail">
              <img src={blog.thumbnail} alt={blog.title} onError={(e) => { e.target.style.display = "none"; }} />
            </div>
          )}

          <article className="fb-article">
            {(blog.blocks || []).map((block, i) => renderBlock(block, i))}
          </article>

          <div className="fb-article-footer">
            <button className="fb-btn fb-btn--outline" onClick={() => navigate("/")}>← Back to All Articles</button>
            <a href="https://shashankupadhyaya.com" className="fb-footer-site">shashankupadhyaya.com ↗</a>
          </div>

        </div>
      </div>
    </div>
  );
}

function renderBlock(block, i) {
  switch (block.type) {
    case "paragraph":
      return block.content ? <p key={i}>{block.content}</p> : null;
    case "h2":
      return block.content ? <h2 key={i}>{block.content}</h2> : null;
    case "h3":
      return block.content ? <h3 key={i}>{block.content}</h3> : null;
    case "quote":
      return block.content ? <blockquote key={i}>{block.content}</blockquote> : null;
    case "divider":
      return <hr key={i} />;
    case "list":
      return block.content ? (
        <ul key={i}>
          {block.content.split("\n").filter(Boolean).map((item, j) => (
            <li key={j}>{item.replace(/^[-•*]\s*/, "")}</li>
          ))}
        </ul>
      ) : null;
    case "code":
      return (
        <div key={i} className="fb-code">
          {block.language && <div className="fb-code-header">{block.language}</div>}
          <pre><code>{block.content}</code></pre>
        </div>
      );
    case "image":
      return block.content ? (
        <figure key={i}>
          <img src={block.content} alt={block.caption || ""} onError={(e) => { e.target.style.display = "none"; }} />
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      ) : null;
    case "video": {
      const ytId = getYouTubeId(block.content);
      return block.content ? (
        <div key={i} className="fb-video">
          {ytId ? (
            <div className="fb-video-ratio">
              <iframe src={`https://www.youtube.com/embed/${ytId}`} title="video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen />
            </div>
          ) : (
            <video src={block.content} controls />
          )}
        </div>
      ) : null;
    }
    default:
      return null;
  }
}

const CSS = `
  .fb {
    --bg: #0A0B0D;
    --card: #0E1116;
    --border: rgba(255,255,255,0.08);
    --text: #EDEFF2;
    --text-body: rgba(237,239,242,0.82);
    --muted: #9AA3AF;
    --gold: #C7F94B;
    --gold-dim: rgba(199,249,75,0.12);
    --nav-h: 60px;
    --radius: 8px;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    font-size: 15px;
    -webkit-font-smoothing: antialiased;
  }
  .fb *, .fb *::before, .fb *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .fb a { color: inherit; text-decoration: none; }
  .fb button { font-family: inherit; cursor: pointer; }
  .fb img { display: block; max-width: 100%; }
  .fb--center {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    min-height: 100vh; gap: 16px;
  }

  .fb-nav {
    position: fixed; top: 3px; left: 0; right: 0; z-index: 49;
    height: var(--nav-h); transition: all 0.25s;
  }
  .fb-nav--solid {
    background: rgba(10,11,13,0.95); backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--border);
  }
  .fb-nav-inner {
    max-width: 780px; margin: 0 auto; padding: 0 24px;
    height: 100%; display: flex; align-items: center; gap: 16px;
  }
  .fb-nav-back {
    font-size: 13px; color: var(--muted); background: none; border: none;
    cursor: pointer; transition: color 0.15s; padding: 0;
  }
  .fb-nav-back:hover { color: var(--gold); }
  .fb-nav-title {
    font-size: 13px; color: var(--muted); flex: 1;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .fb-nav-logo { height: 56px; width: auto; display: block; object-fit: contain; margin-left: auto; }

  .fb-page { padding-top: calc(var(--nav-h) + 16px); }
  .fb-container { max-width: 720px; margin: 0 auto; padding: 0 24px 100px; }

  .fb-header { padding: 40px 0 36px; border-bottom: 1px solid var(--border); margin-bottom: 36px; }
  .fb-chip {
    display: inline-block; font-size: 11px; font-weight: 600; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--gold);
    background: var(--gold-dim); border: 1px solid rgba(199,249,75,0.25);
    padding: 4px 10px; border-radius: 4px; margin-bottom: 20px;
  }
  .fb-title {
    font-family: 'Archivo', sans-serif; font-weight: 900;
    font-size: clamp(26px, 4.5vw, 42px); letter-spacing: -0.03em;
    line-height: 1.12; margin-bottom: 24px;
  }
  .fb-meta-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .fb-author { display: flex; align-items: center; gap: 12px; }
  .fb-author-avatar {
    width: 40px; height: 40px; border-radius: 50%;
    background: var(--gold-dim); border: 2px solid rgba(199,249,75,0.3);
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 700; color: var(--gold); flex-shrink: 0;
  }
  .fb-author-name { font-size: 14px; font-weight: 600; }
  .fb-author-meta { font-size: 12px; color: var(--muted); margin-top: 2px; }

  .fb-thumbnail { border-radius: var(--radius); overflow: hidden; margin-bottom: 48px; border: 1px solid var(--border); }
  .fb-thumbnail img { width: 100%; max-height: 480px; object-fit: cover; }

  .fb-article { font-size: 17px; line-height: 1.85; color: var(--text-body); }
  .fb-article h2 {
    font-family: 'Archivo', sans-serif; font-size: 24px; font-weight: 800;
    letter-spacing: -0.02em; line-height: 1.25; margin: 52px 0 16px; color: var(--text);
  }
  .fb-article h3 {
    font-family: 'Archivo', sans-serif; font-size: 19px; font-weight: 700;
    letter-spacing: -0.01em; margin: 36px 0 12px; color: var(--text);
  }
  .fb-article p { margin: 0 0 24px; }
  .fb-article blockquote {
    border-left: 3px solid var(--gold); margin: 32px 0;
    padding: 16px 24px; background: var(--gold-dim);
    border-radius: 0 var(--radius) var(--radius) 0;
    font-size: 16px; font-style: italic; color: var(--text);
  }
  .fb-article ul, .fb-article ol { padding-left: 28px; margin: 0 0 24px; }
  .fb-article ul { list-style: disc; }
  .fb-article ol { list-style: decimal; }
  .fb-article li { margin-bottom: 8px; display: list-item; }
  .fb-article hr { border: none; border-top: 1px solid var(--border); margin: 48px 0; }
  .fb-article strong { color: var(--text); font-weight: 700; }
  .fb-article a { color: var(--gold); text-decoration: underline; text-underline-offset: 3px; }
  .fb-article figure { margin: 8px 0 32px; }
  .fb-article figure img { width: 100%; border-radius: var(--radius); border: 1px solid var(--border); }
  .fb-article figcaption { margin-top: 10px; font-size: 13px; color: var(--muted); text-align: center; }

  .fb-code { border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; margin: 8px 0 28px; }
  .fb-code-header {
    padding: 8px 16px; background: rgba(255,255,255,0.04); border-bottom: 1px solid var(--border);
    font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted);
    font-family: 'Space Mono', monospace;
  }
  .fb-code pre { margin: 0; padding: 20px; font-size: 13px; line-height: 1.7; overflow-x: auto; }
  .fb-code code { font-family: 'Space Mono', monospace; }

  .fb-video { margin: 8px 0 32px; border-radius: var(--radius); overflow: hidden; border: 1px solid var(--border); }
  .fb-video-ratio { position: relative; padding-bottom: 56.25%; background: #000; }
  .fb-video-ratio iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: none; }
  .fb-video video { width: 100%; display: block; }

  .fb-btn {
    display: inline-flex; align-items: center; font-size: 14px; font-weight: 600;
    padding: 9px 18px; border-radius: 6px; border: none;
    cursor: pointer; transition: all 0.15s; gap: 6px;
  }
  .fb-btn--gold { background: var(--gold); color: #000; }
  .fb-btn--gold:hover { opacity: 0.88; }
  .fb-btn--outline { background: transparent; color: var(--text); border: 1px solid var(--border); }
  .fb-btn--outline:hover { border-color: var(--gold); color: var(--gold); }

  .fb-article-footer {
    margin-top: 64px; padding-top: 28px; border-top: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px;
  }
  .fb-footer-site { font-size: 13px; color: var(--muted); transition: color 0.15s; }
  .fb-footer-site:hover { color: var(--gold); }

  .fb-spinner {
    width: 32px; height: 32px; border-radius: 50%;
    border: 2px solid var(--border); border-top-color: var(--gold);
    animation: fb-spin 0.75s linear infinite;
  }
  @keyframes fb-spin { to { transform: rotate(360deg); } }
  .fb-empty { text-align: center; display: flex; flex-direction: column; align-items: center; gap: 16px; }
  .fb-empty-icon { font-size: 36px; color: var(--muted); }
  .fb-empty p { font-size: 15px; color: var(--muted); }

  @media (max-width: 600px) {
    .fb-container { padding: 0 16px 80px; }
    .fb-article { font-size: 16px; }
    .fb-article h2 { font-size: 20px; }
    .fb-nav-logo { height: 36px; }
    .fb-meta-row { flex-direction: column; align-items: flex-start; }
  }
`;
